/**
 * ThreadItem Component
 *
 * Individual message thread item in the sidebar list.
 */

import React from 'react';
import type { MessageThread } from '../hooks';

interface ThreadItemProps {
  thread: MessageThread;
  isSelected: boolean;
  onClick: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  pending: { label: 'Pending', bg: 'bg-amber-100', color: 'text-amber-700' },
  confirmed: { label: 'Confirmed', bg: 'bg-green-100', color: 'text-green-700' },
  active: { label: 'Active', bg: 'bg-blue-100', color: 'text-blue-700' },
  completed: { label: 'Completed', bg: 'bg-gray-100', color: 'text-gray-600' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-100', color: 'text-red-600' },
};

export const ThreadItem: React.FC<ThreadItemProps> = ({
  thread,
  isSelected,
  onClick,
}) => {
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const status = thread.bookingStatus ? STATUS_CONFIG[thread.bookingStatus] : null;

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
      } ${thread.unreadCount > 0 ? 'bg-blue-50/50' : ''}`}
    >
      <div className="flex items-start gap-3">
        {/* Guest Avatar */}
        <div className="relative flex-shrink-0">
          <img
            src={thread.guestAvatar}
            alt={thread.guestName}
            className="w-12 h-12 rounded-full object-cover"
          />
          {thread.unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary-500 text-white text-xs rounded-full flex items-center justify-center">
              {thread.unreadCount}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className={`font-medium text-gray-900 truncate ${
              thread.unreadCount > 0 ? 'font-semibold' : ''
            }`}>
              {thread.guestName}
            </span>
            <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
              {formatTime(thread.lastMessageAt)}
            </span>
          </div>

          <p className="text-xs text-gray-500 truncate mb-1">
            {thread.listingTitle}
          </p>

          <p className={`text-sm truncate ${
            thread.unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'
          }`}>
            {thread.lastMessageBy === 'host' && (
              <span className="text-gray-400">You: </span>
            )}
            {thread.lastMessage}
          </p>

          {/* Booking Status Badge */}
          {status && (
            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
              {status.label}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default ThreadItem;
